/**
 * Default theme colors and content for the Trade Flow Map Section block.
 */
import mapDataJson from './map-data.json';

const mapData =
	mapDataJson && typeof mapDataJson === 'object' && mapDataJson.default
		? mapDataJson.default
		: mapDataJson || {};

export const THEME_COLORS = {
	primary: '#0a4f55',
	primaryLight: '#F0F6F5',
	white: '#ffffff',
	black: '#000000',
};

export const CATEGORY_COLORS = {
	'minerals-metals': '#983920',
	'agricultural-commodities': '#80BA27',
	'energy-chemicals': '#CAB447',
	'industrial-specialised': '#009BAB',
	'industrial-technical': '#578C95',
	'consumer-retail': '#E88359',
	healthcare: '#397245',
};

/**
 * Stable category metadata (id → SVG class + color + label).
 */
export const CATEGORIES = (
	Array.isArray( mapData.categories ) ? mapData.categories : []
).map( ( category ) => ( {
	id: category.id,
	svgClass: category.slug,
	color: category.color,
	label: category.label,
} ) );

/** Alias used by inspector SelectControl options. */
export const CATEGORY_OPTIONS =
	CATEGORIES.length > 0
		? CATEGORIES
		: [
				{
					id: 'minerals-metals',
					svgClass: 'purple',
					color: CATEGORY_COLORS[ 'minerals-metals' ],
					label: 'Minerals & Metals',
				},
		  ];

export const DEFAULT_NAV_BUTTONS = [
	{
		label: 'Minerals & Metals',
		backgroundColor: CATEGORY_COLORS[ 'minerals-metals' ],
		textColor: '',
		category: 'minerals-metals',
		enableDarkTooltip: false,
	},
	{
		label: 'Agricultural Commodities',
		backgroundColor: CATEGORY_COLORS[ 'agricultural-commodities' ],
		textColor: '',
		category: 'agricultural-commodities',
		enableDarkTooltip: false,
	},
	{
		label: 'Energy & Chemicals',
		backgroundColor: CATEGORY_COLORS[ 'energy-chemicals' ],
		textColor: '',
		category: 'energy-chemicals',
		enableDarkTooltip: false,
	},
	{
		label: 'Industrial & Specialised Products',
		backgroundColor: CATEGORY_COLORS[ 'industrial-specialised' ],
		textColor: '',
		category: 'industrial-specialised',
		enableDarkTooltip: false,
	},
	{
		label: 'Industrial & Technical',
		backgroundColor: CATEGORY_COLORS[ 'industrial-technical' ],
		textColor: '',
		category: 'industrial-technical',
		enableDarkTooltip: false,
	},
	{
		label: 'Consumer & Retail',
		backgroundColor: CATEGORY_COLORS[ 'consumer-retail' ],
		textColor: '',
		category: 'consumer-retail',
		enableDarkTooltip: false,
	},
	{
		label: 'Healthcare',
		backgroundColor: CATEGORY_COLORS.healthcare,
		textColor: '',
		category: 'healthcare',
		enableDarkTooltip: false,
	},
];

export const DEFAULT_NAV_BUTTON = {
	label: 'Category',
	backgroundColor: CATEGORY_COLORS[ 'minerals-metals' ],
	textColor: '',
	category: 'minerals-metals',
	enableDarkTooltip: false,
};

/**
 * Pixel offsets for country labels near crowded regions.
 * Marker x/y stay geographic; only the name is nudged.
 */
export const DEFAULT_LABEL_OFFSETS = {
	'loc-uk': { x: -22, y: -18 },
	'loc-germany': { x: 16, y: -22 },
	'loc-eu': { x: -18, y: 6 },
	'loc-italy': { x: 18, y: 4 },
	'loc-guinea': { x: -24, y: -16 },
	'loc-ghana': { x: 18, y: 2 },
	'loc-nigeria': { x: 8, y: 14 },
	'loc-drc': { x: -18, y: 0 },
	'loc-tanzania': { x: 20, y: 2 },
	'loc-mozambique': { x: 18, y: -4 },
	'loc-south-africa': { x: -8, y: 8 },
	'loc-kuwait': { x: 4, y: -22 },
	'loc-ksa': { x: -22, y: 4 },
	'loc-uae': { x: 10, y: 16 },
	'loc-dubai': { x: 22, y: -16 },
	'loc-india': { x: -16, y: 6 },
	'loc-china': { x: -6, y: -20 },
	'loc-vietnam': { x: 18, y: 6 },
	'loc-south-korea': { x: 10, y: -18 },
	'loc-japan': { x: 24, y: 4 },
	'loc-singapore': { x: 6, y: 10 },
	'loc-bahrain': { x: -20, y: -20 },
	'loc-oman': { x: 22, y: 8 },
	'loc-switzerland': { x: 16, y: 10 },
	'loc-indonesia': { x: 18, y: 8 },
};

export const DEFAULT_LABEL_OFFSETS_BY_LABEL = {
	Bahrain: DEFAULT_LABEL_OFFSETS[ 'loc-bahrain' ],
	Oman: DEFAULT_LABEL_OFFSETS[ 'loc-oman' ],
	Switzerland: DEFAULT_LABEL_OFFSETS[ 'loc-switzerland' ],
	Indonesia: DEFAULT_LABEL_OFFSETS[ 'loc-indonesia' ],
};

/**
 * Seeded label offset for a location id or display name.
 *
 * @param {string} [id]    Location id.
 * @param {string} [label] Country / location name.
 * @return {{ x: number, y: number }} Offset in pixels.
 */
export function getDefaultLabelOffset( id = '', label = '' ) {
	if ( id && DEFAULT_LABEL_OFFSETS[ id ] ) {
		return DEFAULT_LABEL_OFFSETS[ id ];
	}

	if ( label && DEFAULT_LABEL_OFFSETS_BY_LABEL[ label ] ) {
		return DEFAULT_LABEL_OFFSETS_BY_LABEL[ label ];
	}

	return { x: 0, y: 0 };
}

/**
 * Parse a label offset, falling back when the value is missing.
 *
 * @param {*}      value    Stored offset.
 * @param {number} fallback Default when empty/invalid.
 * @return {number} Finite offset.
 */
export function parseLabelOffset( value, fallback = 0 ) {
	if ( value === '' || value === null || typeof value === 'undefined' ) {
		return fallback;
	}

	const parsed = Number( value );
	return Number.isFinite( parsed ) ? parsed : fallback;
}

/** Unique map locations reused by multiple trade flows. */
export const DEFAULT_LOCATIONS = (
	Array.isArray( mapData.locations ) ? mapData.locations : []
).map( ( location ) => {
	const seeded = getDefaultLabelOffset( location.id, location.label );

	return {
		id: location.id,
		label: location.label,
		x: location.x,
		y: location.y,
		labelOffsetX: parseLabelOffset( location.labelOffsetX, seeded.x ),
		labelOffsetY: parseLabelOffset( location.labelOffsetY, seeded.y ),
	};
} );

export const DEFAULT_LOCATION = {
	id: '',
	label: '',
	x: 50,
	y: 50,
	labelOffsetX: 0,
	labelOffsetY: 0,
};

/** Admin-managed origin → destination flows. */
export const DEFAULT_FLOWS = (
	Array.isArray( mapData.flows ) ? mapData.flows : []
).map( ( flow ) => ( {
	id: flow.id,
	category: flow.category,
	fromLocationId: flow.fromLocationId,
	toLocationId: flow.toLocationId,
	enabled: flow.enabled !== false,
} ) );

export const DEFAULT_FLOW = {
	id: '',
	category: 'minerals-metals',
	fromLocationId: '',
	toLocationId: '',
	enabled: true,
};

/** Default map markers from map-data.json (legacy fallback). */
export const DEFAULT_MARKERS = (
	Array.isArray( mapData.markers ) ? mapData.markers : []
).map( ( marker ) => ( {
	id: marker.id,
	label: marker.label,
	tooltipFrom: marker.tooltipFrom,
	tooltipTo: marker.tooltipTo,
	ariaLabel: marker.ariaLabel,
	x: marker.x,
	y: marker.y,
	labelOffsetX: parseLabelOffset( marker.labelOffsetX, 0 ),
	labelOffsetY: parseLabelOffset( marker.labelOffsetY, 0 ),
	category: marker.category,
	enabled: marker.enabled !== false,
} ) );

export const DEFAULT_MARKER = {
	id: '',
	label: '',
	tooltipFrom: '',
	tooltipTo: '',
	ariaLabel: '',
	x: 50,
	y: 50,
	labelOffsetX: 0,
	labelOffsetY: 0,
	category: 'minerals-metals',
	enabled: true,
};

export const MAP_SVG_URL =
	'/wp-content/themes/alteia/assets/src/images/trade-flow-map.svg';

/**
 * Original SVG artboard used when marker x/y percentages were authored.
 * Marker attributes remain in this coordinate space for backward compatibility.
 */
export const MAP_SVG_LEGACY_VIEWBOX = {
	x: 0,
	y: 0,
	width: 1200,
	height: 650,
};

/**
 * Cropped SVG viewBox (blank space removed). Keep in sync with trade-flow-map.svg.
 */
export const MAP_SVG_VIEWBOX = {
	x: 16,
	y: 66,
	width: 1168,
	height: 522,
};

/**
 * Convert marker percentages from the legacy artboard into the cropped viewBox.
 *
 * @param {number} xPercent Horizontal position (0–100) in legacy viewBox space.
 * @param {number} yPercent Vertical position (0–100) in legacy viewBox space.
 * @return {{ x: number, y: number }} Display percentages for the cropped SVG.
 */
export function mapMarkerPercentToViewBox( xPercent, yPercent ) {
	const legacyX = Number( xPercent );
	const legacyY = Number( yPercent );
	const safeX = Number.isFinite( legacyX ) ? legacyX : 50;
	const safeY = Number.isFinite( legacyY ) ? legacyY : 50;

	const svgX =
		MAP_SVG_LEGACY_VIEWBOX.x +
		( safeX / 100 ) * MAP_SVG_LEGACY_VIEWBOX.width;
	const svgY =
		MAP_SVG_LEGACY_VIEWBOX.y +
		( safeY / 100 ) * MAP_SVG_LEGACY_VIEWBOX.height;

	return {
		x: ( ( svgX - MAP_SVG_VIEWBOX.x ) / MAP_SVG_VIEWBOX.width ) * 100,
		y: ( ( svgY - MAP_SVG_VIEWBOX.y ) / MAP_SVG_VIEWBOX.height ) * 100,
	};
}

/**
 * Convert a click on the cropped map preview back to stored marker percentages.
 *
 * @param {number} xPercent Horizontal position (0–100) in cropped viewBox space.
 * @param {number} yPercent Vertical position (0–100) in cropped viewBox space.
 * @return {{ x: number, y: number }} Marker percentages in legacy viewBox space.
 */
export function viewBoxPercentToMarkerPercent( xPercent, yPercent ) {
	const displayX = Number( xPercent );
	const displayY = Number( yPercent );
	const safeX = Number.isFinite( displayX ) ? displayX : 50;
	const safeY = Number.isFinite( displayY ) ? displayY : 50;

	const svgX = MAP_SVG_VIEWBOX.x + ( safeX / 100 ) * MAP_SVG_VIEWBOX.width;
	const svgY = MAP_SVG_VIEWBOX.y + ( safeY / 100 ) * MAP_SVG_VIEWBOX.height;

	return {
		x:
			( ( svgX - MAP_SVG_LEGACY_VIEWBOX.x ) /
				MAP_SVG_LEGACY_VIEWBOX.width ) *
			100,
		y:
			( ( svgY - MAP_SVG_LEGACY_VIEWBOX.y ) /
				MAP_SVG_LEGACY_VIEWBOX.height ) *
			100,
	};
}

export const DEFAULT_COUNTERS = [
	{
		prefix: '',
		number: 1.08,
		suffix: 'BN',
		description: 'USD cash deployed',
		backgroundColor: '',
		numberColor: '',
		descriptionColor: '',
	},
	{
		prefix: '',
		number: 1106,
		suffix: '',
		description: 'Transactions completed',
		backgroundColor: '',
		numberColor: '',
		descriptionColor: '',
	},
	{
		prefix: '',
		number: 68,
		suffix: '+',
		description: 'Unique trade corridors',
		backgroundColor: '',
		numberColor: '',
		descriptionColor: '',
	},
	{
		prefix: '',
		number: 28,
		suffix: '+',
		description: 'Countries',
		backgroundColor: '',
		numberColor: '',
		descriptionColor: '',
	},
	{
		prefix: '',
		number: 26,
		suffix: '+',
		description: 'Goods & commodities traded',
		backgroundColor: '',
		numberColor: '',
		descriptionColor: '',
	},
];

export const DEFAULT_COUNTER = {
	prefix: '',
	number: 0,
	suffix: '+',
	description: '',
	backgroundColor: '',
	numberColor: '',
	descriptionColor: '',
};

export const DEFAULT_HEADING = 'Our trade footprint';

export const DEFAULT_SUBTITLE =
	'Connecting businesses across major global trade routes.';

export const DEFAULT_DESCRIPTION =
	'<p>The interactive map provides a Group-wide view of trade activity across Alteia Fund Management Ltd and Alteia Capital Ltd, highlighting the markets, trade corridors, and goods financed.</p>';

export const DEFAULT_FOOTNOTE = 'Last update: July 2026.';

export const DEFAULT_SECTION_PADDING = {
	top: '80',
	right: '0',
	bottom: '80',
	left: '0',
};

export const DEFAULT_SECTION_MARGIN = {
	top: '0',
	right: '0',
	bottom: '0',
	left: '0',
};

export const DEFAULT_CONTAINER_PADDING = {
	top: '0',
	right: '0',
	bottom: '0',
	left: '0',
};
